import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader, DataTable, EmptyState, ErrorState } from '@ui-kit';
import type { DataTableColumn } from '@ui-kit';
import { StatusBadge } from '../../_shared/components/StatusBadge';
import { useServicesQuery } from '../hooks/useServices';
import { ServiceDetailDialog } from './ServiceDetailDialog';
import type { ServiceRow } from '../types/service';

function TableSkeleton() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)' }}>
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} style={{ height: '48px', background: 'var(--color-surface-muted)', borderRadius: 'var(--radius-md)' }} />
      ))}
    </div>
  );
}

/**
 * Gateway 서비스 목록 (#621) — GET /api/eraf/gateway/services 실 데이터 렌더.
 *
 * BE 에 create/update/delete 엔드포인트가 없으므로 조회 전용이다. 행 클릭 시 상세 다이얼로그로
 * 소속 타겟(+헬스)과 적용 upstream 요약을 확인할 수 있다.
 */
export function ServicesListPage() {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState<ServiceRow | null>(null);

  const { data, isLoading, isError, refetch } = useServicesQuery();

  const filteredRows = useMemo(() => {
    if (!data) return [];
    const needle = q.trim().toLowerCase();
    if (!needle) return data;
    return data.filter(
      (row) =>
        row.name.toLowerCase().includes(needle) ||
        row.serviceKey.toLowerCase().includes(needle) ||
        (row.description ?? '').toLowerCase().includes(needle)
    );
  }, [data, q]);

  const columns: DataTableColumn<ServiceRow>[] = [
    { key: 'name', header: t('gateway.services.columns.name'), width: '180px' },
    {
      key: 'serviceKey',
      header: t('gateway.services.columns.serviceKey'),
      width: '140px',
      render: (row) => (
        <span style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)' }}>
          {row.serviceKey}
        </span>
      ),
    },
    {
      key: 'description',
      header: t('gateway.services.columns.description'),
      render: (row) => (
        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
          {row.description || '-'}
        </span>
      ),
    },
    {
      key: 'status',
      header: t('gateway.services.columns.status'),
      width: '100px',
      render: (row) => <StatusBadge status={row.enabled ? 'enabled' : 'disabled'} size="sm" />,
    },
    {
      key: 'actions',
      header: t('gateway.services.columns.actions'),
      width: '120px',
      render: (row) => (
        <button
          type="button"
          aria-label={`${row.name} ${t('gateway.services.actions.viewDetail')}`}
          onClick={(e) => { e.stopPropagation(); setSelected(row); }}
          style={actionBtnStyle}
        >
          {t('gateway.services.actions.viewDetail')}
        </button>
      ),
    },
  ];

  return (
    <div>
      {/* R9-E-006: 사이드바 미노출 URL-only 접근 안내 */}
      <div
        role="note"
        aria-label={t('gateway.services.sidebarHiddenNote')}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-2)',
          padding: 'var(--spacing-2) var(--spacing-4)',
          background: 'var(--color-info-subtle, #EEF2FF)',
          border: '1px solid var(--color-info-border, #C7D2FE)',
          borderBottom: 'none',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-info-text, #3730A3)',
        }}
      >
        <span aria-hidden="true">i</span>
        <span>{t('gateway.services.sidebarHiddenNote')}</span>
      </div>

      <PageHeader title={t('gateway.services.title')} breadcrumb={t('gateway.services.breadcrumb')} />

      {/* Search */}
      <div style={{ display: 'flex', gap: 'var(--spacing-3)', padding: 'var(--spacing-4) var(--spacing-6)', flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          type="search"
          aria-label={t('gateway.services.filter.search')}
          placeholder={t('gateway.services.filter.search')}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{ ...selectStyle, flex: 1, minWidth: '200px' }}
        />
      </div>

      {/* Main Content */}
      <div style={{ padding: '0 var(--spacing-6) var(--spacing-6)' }}>
        {isLoading ? (
          <TableSkeleton />
        ) : isError ? (
          <ErrorState
            title={t('gateway.services.list.error')}
            message={t('gateway.shared.error.networkErrorMessage')}
            onRetry={() => refetch()}
          />
        ) : filteredRows.length === 0 ? (
          <EmptyState title={t('gateway.services.list.empty')} description={t('gateway.services.list.emptyDesc')} />
        ) : (
          <DataTable
            columns={columns}
            rows={filteredRows}
            rowKey={(row) => row.id}
            onRowClick={(row) => setSelected(row)}
          />
        )}
      </div>

      {/* Detail Dialog */}
      {selected && (
        <ServiceDetailDialog
          serviceId={selected.id}
          serviceName={selected.name}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}

const actionBtnStyle: React.CSSProperties = {
  padding: 'var(--spacing-1) var(--spacing-2)',
  background: 'var(--color-surface-background)',
  color: 'var(--color-text-secondary)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-sm)',
  cursor: 'pointer',
  fontSize: 'var(--font-size-xs)',
};

const selectStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-3)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  background: 'var(--color-surface-background)',
};
